import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useAuth } from './AuthContext';

type OperationalTabMode = 'idle' | 'leader' | 'blocked';
type OperationalTabBlockingReason = 'other_active_tab' | 'taken_over' | null;

interface OperationalTabLease {
  tabId: string;
  sessionId: string;
  claimedAt: string;
  lastSeenAt: string;
}

interface OperationalTabBroadcastMessage {
  type: 'lease_claimed' | 'lease_released';
  sessionId: string;
  tabId: string;
  takeover?: boolean;
  timestamp: string;
}

interface OperationalTabContextValue {
  isReady: boolean;
  isEnforced: boolean;
  isLeader: boolean;
  canOperateInThisTab: boolean;
  shouldBlockApp: boolean;
  decisionRequired: boolean;
  blockingReason: OperationalTabBlockingReason;
  tabId: string | null;
  takeOverHere: () => void;
  keepUsingOtherTab: () => void;
}

const OperationalTabContext = createContext<OperationalTabContextValue | undefined>(undefined);

const OPERATIONAL_TAB_STORAGE_KEY_PREFIX = 'contacttel.operationalTabLease:';
const OPERATIONAL_TAB_BROADCAST_CHANNEL = 'contacttel-operational-tab';
const OPERATIONAL_TAB_HEARTBEAT_INTERVAL_MS = 4_000;
const OPERATIONAL_TAB_LEASE_TTL_MS = 12_000;

function buildOperationalTabStorageKey(sessionId: string): string {
  return `${OPERATIONAL_TAB_STORAGE_KEY_PREFIX}${sessionId}`;
}

function createOperationalTabId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function parseOperationalTabLease(rawValue: string | null): OperationalTabLease | null {
  if (!rawValue) {
    return null;
  }
  try {
    const parsed = JSON.parse(rawValue) as Partial<OperationalTabLease>;
    const tabId = String(parsed.tabId || '').trim();
    const sessionId = String(parsed.sessionId || '').trim();
    const claimedAt = String(parsed.claimedAt || '').trim();
    const lastSeenAt = String(parsed.lastSeenAt || '').trim();
    if (!tabId || !sessionId || !claimedAt || !lastSeenAt) {
      return null;
    }
    return {
      tabId,
      sessionId,
      claimedAt,
      lastSeenAt,
    };
  } catch {
    return null;
  }
}

function isOperationalTabLeaseFresh(
  lease: OperationalTabLease | null,
  nowMs = Date.now(),
): boolean {
  if (!lease) {
    return false;
  }
  const lastSeenAtMs = Date.parse(lease.lastSeenAt);
  if (!Number.isFinite(lastSeenAtMs)) {
    return false;
  }
  return nowMs - lastSeenAtMs <= OPERATIONAL_TAB_LEASE_TTL_MS;
}

/**
 * Coordinate one operational tab per authenticated session inside one browser profile.
 *
 * The backend single-session rule already prevents multiple active sessions on
 * different browsers/devices. This provider complements that contract locally:
 * within one browser profile, only one tab may behave as the operational tab
 * that owns SIP/runtime responsibilities. Secondary tabs stay blocked until the
 * user explicitly takes over.
 */
export function OperationalTabProvider({ children }: { children: ReactNode }) {
  const { token, sessionId } = useAuth();
  const tabId = useMemo(() => (typeof window === 'undefined' ? null : createOperationalTabId()), []);
  const [mode, setMode] = useState<OperationalTabMode>('idle');
  const [isReady, setIsReady] = useState(false);
  const [blockingReason, setBlockingReason] = useState<OperationalTabBlockingReason>(null);
  const [decisionRequired, setDecisionRequired] = useState(false);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const blockingReasonRef = useRef<OperationalTabBlockingReason>(null);
  const decisionRequiredRef = useRef(false);

  const isEnforced = Boolean(token && sessionId && tabId);

  const broadcastMessage = useCallback((message: OperationalTabBroadcastMessage) => {
    if (!broadcastChannelRef.current) {
      return;
    }
    try {
      broadcastChannelRef.current.postMessage(message);
    } catch {
      // no-op
    }
  }, []);

  const applyLeaderState = useCallback(() => {
    blockingReasonRef.current = null;
    decisionRequiredRef.current = false;
    setMode('leader');
    setBlockingReason(null);
    setDecisionRequired(false);
    setIsReady(true);
  }, []);

  const applyBlockedState = useCallback((nextReason: OperationalTabBlockingReason, nextDecisionRequired: boolean) => {
    blockingReasonRef.current = nextReason;
    decisionRequiredRef.current = nextDecisionRequired;
    setMode('blocked');
    setBlockingReason(nextReason);
    setDecisionRequired(nextDecisionRequired);
    setIsReady(true);
  }, []);

  const readCurrentLease = useCallback((): OperationalTabLease | null => {
    if (typeof window === 'undefined' || !sessionId) {
      return null;
    }
    const storageKey = buildOperationalTabStorageKey(sessionId);
    return parseOperationalTabLease(window.localStorage.getItem(storageKey));
  }, [sessionId]);

  const writeLease = useCallback((nextLease: OperationalTabLease) => {
    if (typeof window === 'undefined') {
      return;
    }
    const storageKey = buildOperationalTabStorageKey(nextLease.sessionId);
    window.localStorage.setItem(storageKey, JSON.stringify(nextLease));
  }, []);

  const clearLeaseIfOwnedByCurrentTab = useCallback(() => {
    if (typeof window === 'undefined' || !sessionId || !tabId) {
      return;
    }
    const storageKey = buildOperationalTabStorageKey(sessionId);
    const currentLease = parseOperationalTabLease(window.localStorage.getItem(storageKey));
    if (currentLease?.tabId !== tabId) {
      return;
    }
    window.localStorage.removeItem(storageKey);
    broadcastMessage({
      type: 'lease_released',
      sessionId,
      tabId,
      timestamp: new Date().toISOString(),
    });
  }, [broadcastMessage, sessionId, tabId]);

  const claimLeadership = useCallback((takeover: boolean) => {
    if (!sessionId || !tabId) {
      return;
    }
    const currentLease = readCurrentLease();
    const nowIso = new Date().toISOString();
    const nextLease: OperationalTabLease = {
      tabId,
      sessionId,
      claimedAt: currentLease?.tabId === tabId ? currentLease.claimedAt : nowIso,
      lastSeenAt: nowIso,
    };
    writeLease(nextLease);
    broadcastMessage({
      type: 'lease_claimed',
      sessionId,
      tabId,
      takeover,
      timestamp: nowIso,
    });
    applyLeaderState();
  }, [applyLeaderState, broadcastMessage, readCurrentLease, sessionId, tabId, writeLease]);

  const evaluateOperationalTabLeadership = useCallback((contextReason: 'bootstrap' | 'interval' | 'storage' | 'broadcast') => {
    if (!isEnforced || !sessionId || !tabId) {
      setMode('idle');
      setBlockingReason(null);
      setDecisionRequired(false);
      setIsReady(true);
      return;
    }

    const currentLease = readCurrentLease();
    const leaseIsFresh = isOperationalTabLeaseFresh(currentLease);
    if (!leaseIsFresh) {
      claimLeadership(false);
      return;
    }

    if (currentLease?.tabId === tabId) {
      applyLeaderState();
      return;
    }

    const nextReason = blockingReasonRef.current === 'taken_over'
      ? 'taken_over'
      : 'other_active_tab';
    const nextDecisionRequired = blockingReasonRef.current === 'taken_over'
      ? false
      : (decisionRequiredRef.current || contextReason === 'bootstrap');
    applyBlockedState(nextReason, nextDecisionRequired);
  }, [applyBlockedState, applyLeaderState, claimLeadership, isEnforced, readCurrentLease, sessionId, tabId]);

  const renewLeadershipLease = useCallback(() => {
    if (!sessionId || !tabId) {
      return;
    }
    const currentLease = readCurrentLease();
    if (
      currentLease
      && isOperationalTabLeaseFresh(currentLease)
      && currentLease.tabId !== tabId
    ) {
      applyBlockedState('taken_over', false);
      return;
    }

    const nowIso = new Date().toISOString();
    const nextLease: OperationalTabLease = {
      tabId,
      sessionId,
      claimedAt: currentLease?.tabId === tabId ? currentLease.claimedAt : nowIso,
      lastSeenAt: nowIso,
    };
    writeLease(nextLease);
  }, [applyBlockedState, readCurrentLease, sessionId, tabId, writeLease]);

  const takeOverHere = useCallback(() => {
    claimLeadership(true);
  }, [claimLeadership]);

  const keepUsingOtherTab = useCallback(() => {
    applyBlockedState('other_active_tab', false);
  }, [applyBlockedState]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    if (!('BroadcastChannel' in window)) {
      broadcastChannelRef.current = null;
      return undefined;
    }
    const channel = new BroadcastChannel(OPERATIONAL_TAB_BROADCAST_CHANNEL);
    broadcastChannelRef.current = channel;
    return () => {
      channel.close();
      if (broadcastChannelRef.current === channel) {
        broadcastChannelRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!isEnforced) {
      return undefined;
    }

    const releaseCurrentLease = () => {
      clearLeaseIfOwnedByCurrentTab();
    };

    window.addEventListener('pagehide', releaseCurrentLease);
    window.addEventListener('beforeunload', releaseCurrentLease);

    return () => {
      window.removeEventListener('pagehide', releaseCurrentLease);
      window.removeEventListener('beforeunload', releaseCurrentLease);
    };
  }, [clearLeaseIfOwnedByCurrentTab, isEnforced]);

  useEffect(() => {
    if (!isEnforced) {
      clearLeaseIfOwnedByCurrentTab();
      blockingReasonRef.current = null;
      decisionRequiredRef.current = false;
      setMode('idle');
      setBlockingReason(null);
      setDecisionRequired(false);
      setIsReady(true);
      return undefined;
    }

    setIsReady(false);
    evaluateOperationalTabLeadership('bootstrap');

    const heartbeat = window.setInterval(() => {
      if (mode === 'leader') {
        renewLeadershipLease();
        return;
      }
      evaluateOperationalTabLeadership('interval');
    }, OPERATIONAL_TAB_HEARTBEAT_INTERVAL_MS);

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== buildOperationalTabStorageKey(sessionId!)) {
        return;
      }
      evaluateOperationalTabLeadership('storage');
    };

    const handleBroadcastMessage = (event: MessageEvent<OperationalTabBroadcastMessage>) => {
      const payload = event.data;
      if (!payload || payload.sessionId !== sessionId || payload.tabId === tabId) {
        return;
      }
      if (payload.type === 'lease_claimed' && payload.takeover) {
        applyBlockedState('taken_over', false);
        return;
      }
      evaluateOperationalTabLeadership('broadcast');
    };

    window.addEventListener('storage', handleStorage);
    broadcastChannelRef.current?.addEventListener('message', handleBroadcastMessage);

    return () => {
      window.clearInterval(heartbeat);
      window.removeEventListener('storage', handleStorage);
      broadcastChannelRef.current?.removeEventListener('message', handleBroadcastMessage);
    };
  }, [
    applyBlockedState,
    clearLeaseIfOwnedByCurrentTab,
    evaluateOperationalTabLeadership,
    isEnforced,
    mode,
    renewLeadershipLease,
    sessionId,
    tabId,
  ]);

  const value = useMemo<OperationalTabContextValue>(() => {
    const isLeader = !isEnforced || mode === 'leader';
    const canOperateInThisTab = !isEnforced || (isReady && isLeader);
    return {
      isReady,
      isEnforced,
      isLeader,
      canOperateInThisTab,
      shouldBlockApp: isEnforced && isReady && mode === 'blocked',
      decisionRequired,
      blockingReason,
      tabId,
      takeOverHere,
      keepUsingOtherTab,
    };
  }, [
    blockingReason,
    decisionRequired,
    isEnforced,
    isReady,
    keepUsingOtherTab,
    mode,
    tabId,
    takeOverHere,
  ]);

  return (
    <OperationalTabContext.Provider value={value}>
      {children}
    </OperationalTabContext.Provider>
  );
}

export function useOperationalTab() {
  const context = useContext(OperationalTabContext);
  if (!context) {
    throw new Error('useOperationalTab must be used within OperationalTabProvider');
  }
  return context;
}
